import { baseCreateUrl } from './cloudinary.common';

export const BASE_API_URL = 'https://cdn.apartmenttherapy.info/image/upload';

// programmatically escape . and /
const prefix = BASE_API_URL.replace(/([./])/g, '\\$1');

// specifying the image types is a workaround that allows us to craft a regexp
// that works correctly with extra periods in the file name.
const imgTypes = ['gif', 'png', 'jpe?g', 'webp'].join('|');

// URL structure is:
//
// https://res.cloudinary.com/<cloud_name>/<resource_type>/<type>/<transformations>/<version>/<public_id>.<format>
//
// everything up to and including <type> is BASE_API_URL.
//
// we have to split apart the URL because we need to apply additional
// context-specific transforms to our images. here is the plan:
//
// * ignore BASE_API_URL; we will reapply it later
// * capture any existing transforms
// * ignore optional version timestamp for now (effectively a cachebuster)
// * capture the public ID _without_ the file extension to avoid format
//   confusion. must be non-greedy!
//
// NOTE: the only capturing groups are in the SERVICE_REGEXP. the rest are
// non-capturing groups.
//
const op = '(?:[a-z]+_[a-z0-9% :]+)';
const ops = `(?:${op}(?:,${op})*)`;
const transforms = `/${ops}(?:/${ops})*`;
const version = '(?:/v\\d+)?';
const pubId = '(?:/[^/]+?)+?';
const ext = `(?:\\.(?:${imgTypes}))?`;

export const SERVICE_REGEXP = new RegExp(
  `^${prefix}(${transforms})?${version}(${pubId})${ext}$`,
  'i'
);

/**
 * returns true if this service can handle the passed image URL
 *
 * @export
 * @param {String} url fully-qualified  URL to image
 * @returns {Boolean}
 */
export function testUrl(url) {
  return SERVICE_REGEXP.test(url);
}

/**
 * returns a cloudinary URL for an image already stored on cloudinary, possibly
 * with transforms already applied.
 *
 * see https://cloudinary.com/documentation/image_transformations
 *
 * we expect a imageUrl with BASE_API_URL as a prefix.
 *
 * accepts renderOps object or array of renderOps, which is henceforth called
 * "transforms".
 *
 * @export
 * @param {String} imageUrl canonical image URL
 * @param {Object|Object[]} [options={}] service-agnostic render options (or an array of render options)
 * @returns {String} cloudinary image URL with translated renderOptions
 */
export function createUrl(imageUrl, options = {}) {
  const res = SERVICE_REGEXP.exec(imageUrl);

  // this _must_ match because we already tested it, so we're being a little
  // lazy and just assuming res[2] exists.
  //
  // NOTE: we're substring-ing the match because baseCreateUrl() handles the
  // addition of slashes between the key portions of the URL.
  const existingTransforms = res[1] && res[1].substring(1);
  const publicId = res[2].substring(1);

  // Encode ids so spaces, etc, will not break the call. Unfortunately some
  // ids are already encoded, or partially encoded, but there is no good way to
  // tell. To prevent double encoding we need to decodeURIComponent first.
  let encodedPublicId = publicId;

  try {
    // Protect against "URIError: URI malformed"
    encodedPublicId = encodeURIComponent(decodeURIComponent(publicId));
  } catch (e) {
    console.error(e); // eslint-disable-line
  }

  return baseCreateUrl.call(
    null,
    BASE_API_URL,
    encodedPublicId,
    options,
    existingTransforms
  );
}

/** consumers import default; specs import named exports */
export default {
  testUrl,
  createUrl
};
